import {useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {
    CreateTaxonomyApprovalOperationSerializer,
    TaxonomyApprovalRequestStateSerializer,
} from "~/services/classes/TaxonomyApproval";
import {notificationStore} from "~/stores/init";
import {
    taxonomyApprovalStateHistoryUrl,
    taxonomyApprovalStateNextUrl,
} from "~/pages/taxonomy-builder-v2/services/taxonomyBuilderUrls";
import {taxonomyApprovalStateQueryKey} from "./useTaxonomyApprovalQueries";

export type SaveApprovalStateVariables = {
    state: m_taxonomy.Tree;
    operation: m_taxonomy.Operation;
    /** true for a revert, as the current approval page saves it; null otherwise */
    isRevert: boolean | null;
    /** Shown once saved; none when undefined */
    successMessage?: string;
};

const showError = (approvalId: number, message: string) => notificationStore.show(message, {
    severity: 'error',
    id: `taxonomy-approval-state-error-${approvalId}`,
    autoHideDuration: 6000,
});

/**
 * The approver's copy of the tree: save a new state (`state/next/`, a revert or a note) and undo / redo
 * (`state/history/`), as the current approval page does. Each response is the new copy with its history,
 * so it replaces the cached one. Undo goes to the entry before the latest one up to the active entry (the
 * first can't be undone), redo to the first entry after it.
 */
export function useTaxonomyApprovalState(approvalId: number, data: TaxonomyApprovalRequestStateSerializer | undefined) {
    const queryClient = useQueryClient();
    const onSaved = (updated: TaxonomyApprovalRequestStateSerializer) =>
        queryClient.setQueryData(taxonomyApprovalStateQueryKey(approvalId), updated);

    const save = useMutation({
        mutationFn: async ({state, operation, isRevert}: SaveApprovalStateVariables) => {
            if (!data?.active) throw new Error('The approval has no active state');
            const body: CreateTaxonomyApprovalOperationSerializer = {
                history_number: data.active.history_number + 1,
                history_name: operation.valueOf(),
                history_is_revert: isRevert,
                next_node_id: data.active.next_node_id,
                state,
            };
            return (await queryHttp.put<TaxonomyApprovalRequestStateSerializer>(taxonomyApprovalStateNextUrl(approvalId), body)).data;
        },
        onSuccess: (updated, {successMessage}) => {
            onSaved(updated);
            if (successMessage) {
                notificationStore.show(successMessage, {
                    severity: 'success',
                    id: `taxonomy-approval-state-success-${approvalId}`,
                    autoHideDuration: 3000,
                });
            }
        },
        onError: () => showError(approvalId, 'Failed to save the change. Please try again.'),
    });

    const goto = useMutation({
        mutationFn: async (historyNumber: number) =>
            (await queryHttp.put<TaxonomyApprovalRequestStateSerializer>(taxonomyApprovalStateHistoryUrl(approvalId), {history_number: historyNumber})).data,
        onSuccess: onSaved,
        onError: () => showError(approvalId, 'Failed to undo or redo. Please try again.'),
    });

    const active = data?.active?.history_number;
    const entries = [...(data?.history ?? [])].sort((a, b) => a.history_number - b.history_number);
    const undoable = active === undefined ? [] : entries.filter((e, i) => i >= 1 && e.history_number <= active);
    const undoTo = undoable.length === 0 ? undefined : entries[entries.indexOf(undoable[undoable.length - 1]) - 1];
    const redoTo = active === undefined ? undefined : entries.find(e => e.history_number > active);
    const isPending = save.isPending || goto.isPending;

    return {
        save: save.mutate,
        isPending,
        canUndo: !isPending && undoTo !== undefined,
        canRedo: !isPending && redoTo !== undefined,
        undo: () => undoTo && goto.mutate(undoTo.history_number),
        redo: () => redoTo && goto.mutate(redoTo.history_number),
    };
}
